import React, { useState } from "react";
import { css } from "@emotion/css";
import { PendingIndicator } from "../../components/PendingIndicator";
import type { ArcTextureLensSpec } from "../../types/arcGenerationSession";

// ── Styles ──────────────────────────────────────────────────────────

const listStyle = css`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin: 6px 0 16px;
`;

const cardStyle = css`
  text-align: left;
  padding: 14px 16px;
  border: 1px solid var(--color-border);
  border-radius: 12px;
  background: var(--color-bg-elevated);
  color: var(--color-text);
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease, transform 0.06s ease;
  &:hover:not(:disabled) { border-color: var(--color-primary); background: var(--color-bg-raised); }
  &:active:not(:disabled) { transform: translateY(1px); }
  &:disabled { opacity: 0.55; cursor: not-allowed; }
`;

const cardSelectedStyle = css`
  border-color: var(--color-primary) !important;
  background: var(--color-primary-muted) !important;
  box-shadow: 0 0 0 1px var(--color-primary) inset;
`;

const titleStyle = css`
  font-size: 0.98em;
  font-weight: 650;
  line-height: 1.3;
  display: flex;
  align-items: flex-start;
  gap: 9px;
`;

const bodyStyle = css`
  font-size: 0.86em;
  color: var(--color-text-secondary);
  line-height: 1.5;
  margin-top: 5px;
  margin-left: 23px;
`;

const checkStyle = css`
  color: var(--color-primary);
  width: 14px;
  flex-shrink: 0;
`;

const continueBtnStyle = css`
  width: 100%;
  padding: 12px;
  border: none;
  border-radius: 10px;
  background: var(--color-primary);
  color: var(--color-text-inverse);
  font-size: 0.98em;
  font-weight: 650;
  cursor: pointer;
  &:hover:not(:disabled) { background: var(--color-primary-hover); }
  &:disabled { opacity: 0.55; cursor: not-allowed; }
`;

// ── Component ───────────────────────────────────────────────────────

export interface TextureLensProps {
  spec: ArcTextureLensSpec;
  isStreaming: boolean;
  disabled?: boolean;
  /** Up to two statements the learner recognized. */
  onSubmit: (statementIds: string[], statementTitles: string[]) => void;
}

const MAX_PICKS = 2;

/** "Which version feels closest?" — the learner picks one or two of the
 * synthesized texture statements. */
export const TextureLens: React.FC<TextureLensProps> = ({
  spec, isStreaming, disabled, onSubmit,
}) => {
  const [picks, setPicks] = useState<string[]>([]);
  const statements = spec.statements ?? [];

  const toggle = (id: string) => {
    if (disabled) return;
    setPicks((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= MAX_PICKS) return [...prev.slice(1), id]; // keep most recent two
      return [...prev, id];
    });
  };

  return (
    <div data-testid="texture-lens">
      <div className={listStyle}>
        {statements.map((s, i) => {
          const sel = picks.includes(s.itemId);
          return (
            <button
              key={s.itemId}
              type="button"
              className={sel ? `${cardStyle} ${cardSelectedStyle}` : cardStyle}
              aria-pressed={sel}
              disabled={disabled}
              onClick={() => toggle(s.itemId)}
              data-testid={`texture-statement-${i}`}
            >
              <span className={titleStyle}>
                <span className={checkStyle}>{sel ? "✓" : ""}</span>
                <span>{s.title}</span>
              </span>
              {s.body ? <div className={bodyStyle}>{s.body}</div> : null}
            </button>
          );
        })}
      </div>

      {isStreaming && statements.length === 0 && (
        <PendingIndicator variant="block" label="Reading what you tapped…" data-testid="texture-lens-loading" />
      )}

      {!isStreaming && (
        <button
          type="button"
          className={continueBtnStyle}
          disabled={disabled || picks.length === 0}
          data-testid="texture-lens-continue"
          onClick={() =>
            onSubmit(
              picks,
              picks
                .map((id) => statements.find((s) => s.itemId === id)?.title ?? id)
                .filter(Boolean),
            )
          }
        >
          {picks.length === 0 ? "Pick one or two" : "Continue"}
        </button>
      )}
    </div>
  );
};
